import React from "react";
import { PurchaseItem, Report } from "types";
import { SourceCellRenderer } from "../ColumnTypes";
import { CustomMerchandiseProductFilter } from "../CustomFilters/CustomProductFilter";
import { CustomReport } from "../CustomReport";
import { ReportColumnDef, ReportColumnFilterValueType } from "../SuggestedReports";
import { MerchandiseGrossSalesProductCell } from "./MerchandiseGrossSalesProductCell";
import { getPurchasedWareLineItems } from "@/models/MerchandisePurchaseItem";

export const MerchandiseGrossSalesReport = ({ report }: { report: Report }) => {
    const columns = React.useMemo(() => getColumnDefs({ report }), [report]);

    return (
        <CustomReport excludeFutureDates dateFields={["createdAt"]} report={report} columns={columns} unwind={unwind} />
    );
};

export const unwind = (data: PurchaseItem[]) => {
    const unwoundData = [];

    for (const item of data) {
        if (Array.isArray(item.lineItems)) {
            const wareLineItems = getPurchasedWareLineItems(item);

            for (const lineItem of wareLineItems) {
                unwoundData.push({ ...item, lineItem });
            }
        }
    }

    return unwoundData;
};

interface GetColumnDefsParams {
    report: Report;
}

const getColumnDefs = ({ report }: GetColumnDefsParams): ReportColumnDef[] => [
    {
        field: "purchase.id",
        headerName: "Purchase ID",
        type: ["objectid", "purchase"],
    },
    {
        field: "id",
        headerName: "Item ID",
        type: ["objectid", "purchase"],
    },
    {
        field: "createdAt",
        headerName: "Purchase Date",
        type: "datetime",
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
    },
    {
        field: "product",
        headerName: "Product",
        type: "product",
        filter: CustomMerchandiseProductFilter,
        filterParams: {
            report,
        },
        cellRenderer: MerchandiseGrossSalesProductCell,
        flex: 3,
        minWidth: 400,
        suppressAutoSize: true,
    },
    {
        field: "lineItem.code",
        headerName: "SKU",
        minWidth: 150,
    },
    {
        field: "lineItem.price",
        headerName: "Item Price",
        type: "currency",
        filter: false,
    },
    {
        field: "lineItem.quantity",
        headerName: "Quantity",
    },
    {
        field: "lineItem.amount",
        headerName: "Gross Total",
        type: "currency",
        filter: false,
    },
    {
        field: "source",
        headerName: "Source",
        type: "source",
        cellRenderer: SourceCellRenderer,
    },
];
